/**
 * Offline-First Draft Manager
 * Saves drafts to localStorage immediately, then syncs to server in background.
 * Ensures data is never lost even when server is unavailable.
 */

// Get all pending drafts that need server sync
export const getPendingDrafts = () => {
  try {
    const pendingStr = localStorage.getItem('pendingDrafts');
    return pendingStr ? JSON.parse(pendingStr) : {};
  } catch (e) {
    console.error('Error reading pending drafts:', e);
    return {};
  }
};

// Save a draft to localStorage immediately
export const saveDraftLocally = (key, data, metadata = {}) => {
  try {
    const timestamp = new Date().toISOString();
    const draftEntry = {
      data,
      metadata: {
        ...metadata,
        savedAt: timestamp,
        lastModified: timestamp,
        syncStatus: 'pending'
      }
    };
    
    // Save individual draft
    localStorage.setItem(`draft_${key}`, JSON.stringify(draftEntry));
    
    // Add to pending drafts queue for background sync
    const pending = getPendingDrafts();
    pending[key] = {
      savedAt: timestamp,
      syncStatus: 'pending',
      retryCount: 0
    };
    localStorage.setItem('pendingDrafts', JSON.stringify(pending));
    
    return { success: true, savedAt: timestamp };
  } catch (e) {
    console.error('Error saving draft locally:', e);
    // If localStorage is full, try to clear old drafts
    if (e.name === 'QuotaExceededError') {
      clearOldDrafts();
      // Retry once
      try {
        localStorage.setItem(`draft_${key}`, JSON.stringify({ data, metadata }));
        return { success: true, savedAt: new Date().toISOString() };
      } catch (e2) {
        return { success: false, error: 'Storage quota exceeded' };
      }
    }
    return { success: false, error: e.message };
  }
};

// Load a draft from localStorage
export const loadDraftLocally = (key) => {
  try {
    const draftStr = localStorage.getItem(`draft_${key}`);
    if (!draftStr) return null;
    
    const draft = JSON.parse(draftStr);
    return draft;
  } catch (e) {
    console.error('Error loading draft:', e);
    return null;
  }
};

// Remove a draft from localStorage (after successful sync or manual clear)
export const removeDraftLocally = (key) => {
  try {
    localStorage.removeItem(`draft_${key}`);
    
    // Remove from pending queue
    const pending = getPendingDrafts();
    delete pending[key];
    localStorage.setItem('pendingDrafts', JSON.stringify(pending));
    
    return { success: true };
  } catch (e) {
    console.error('Error removing draft:', e);
    return { success: false };
  }
};

// Mark a draft as synced
export const markDraftSynced = (key) => {
  try {
    const draftStr = localStorage.getItem(`draft_${key}`);
    if (draftStr) {
      const draft = JSON.parse(draftStr);
      draft.metadata.syncStatus = 'synced';
      draft.metadata.syncedAt = new Date().toISOString();
      localStorage.setItem(`draft_${key}`, JSON.stringify(draft));
    }
    
    // Update pending queue
    const pending = getPendingDrafts();
    if (pending[key]) {
      pending[key].syncStatus = 'synced';
      pending[key].syncedAt = new Date().toISOString();
      localStorage.setItem('pendingDrafts', JSON.stringify(pending));
    }
    
    return { success: true };
  } catch (e) {
    console.error('Error marking draft synced:', e);
    return { success: false };
  }
};

// Mark sync failure with retry count
export const markDraftSyncFailed = (key, error) => {
  try {
    const pending = getPendingDrafts();
    if (pending[key]) {
      pending[key].syncStatus = 'failed';
      pending[key].lastError = error;
      pending[key].retryCount = (pending[key].retryCount || 0) + 1;
      pending[key].lastAttempt = new Date().toISOString();
      localStorage.setItem('pendingDrafts', JSON.stringify(pending));
    }
    return { success: true };
  } catch (e) {
    console.error('Error marking draft failed:', e);
    return { success: false };
  }
};

// Clear old drafts (older than 30 days)
export const clearOldDrafts = () => {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('draft_')) {
        try {
          const draft = JSON.parse(localStorage.getItem(key));
          if (draft?.metadata?.savedAt) {
            const savedDate = new Date(draft.metadata.savedAt);
            if (savedDate < thirtyDaysAgo) {
              keysToRemove.push(key);
            }
          }
        } catch (e) {
          // Invalid draft, mark for removal
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    return { removed: keysToRemove.length };
  } catch (e) {
    console.error('Error clearing old drafts:', e);
    return { removed: 0 };
  }
};

// Get all local drafts for a specific type
export const getAllDraftsOfType = (typePrefix) => {
  try {
    const drafts = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(`draft_${typePrefix}`)) {
        try {
          const draft = JSON.parse(localStorage.getItem(key));
          drafts.push({
            key: key.replace('draft_', ''),
            ...draft
          });
        } catch (e) {
          // Skip invalid entries
        }
      }
    }
    return drafts;
  } catch (e) {
    console.error('Error getting drafts:', e);
    return [];
  }
};

// Check if server is available
export const checkServerAvailability = async (apiUrl) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(`${apiUrl}/health`, {
      method: 'GET',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (e) {
    return false;
  }
};

// Generate a unique draft key
export const generateDraftKey = (type, ...identifiers) => {
  return `${type}_${identifiers.filter(Boolean).join('_')}`;
};
